/**
 * Darstellung der Vertragsstatus des Mietvertrag-Generators:
 * deutscher Text und Bootstrap-Badge-Farbe je Status.
 *
 * Die Statuswerte kommen roh aus der mvgen-Datenbank (gemischt deutsch/
 * englisch benannt). Unbekannte Werte werden bewusst unveraendert
 * angezeigt, statt sie zu verschlucken: So faellt ein neuer mvgen-Status
 * im Betrieb sofort auf.
 */

/**
 * Bootstrap-Kontextfarben, die als Badge-Hintergrund (bg) zulaessig sind.
 * Union statt string: Ein Tippfehler ("succes") wuerde sonst erst im
 * Browser als farbloser Badge auffallen.
 */
export type BadgeVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

/** Anzeige-Eigenschaften eines Vertragsstatus. */
export interface ContractStatusPresentation {
  /** Deutscher Anzeigetext. */
  readonly label: string;
  /** Bootstrap-Variante des Badges (bg). */
  readonly variant: BadgeVariant;
  /** true, wenn der Badge dunkle Schrift braucht (helle Hintergruende). */
  readonly darkText: boolean;
}

/**
 * Bekannte Status des Mietvertrag-Generators.
 *
 * Bewusst eine Map und kein Objekt-Literal: Ein Statuswert aus der
 * Datenbank ist Fremdeingabe. Bei einem Objekt lieferte der Zugriff mit
 * "toString" oder "constructor" einen geerbten Prototype-Wert — der
 * landete als React-Child in der Anzeige und erzeugte einen
 * console.error (den die E2E-Fixture als Fehler wertet).
 */
const STATUS_PRESENTATIONS: ReadonlyMap<string, ContractStatusPresentation> = new Map([
  ['DRAFT', { label: 'Entwurf', variant: 'secondary', darkText: false }],
  ['UNVOLLSTAENDIG', { label: 'Unvollständig', variant: 'warning', darkText: true }],
  ['ACTIVE', { label: 'Aktiv', variant: 'success', darkText: false }],
  ['FINALISIERT', { label: 'Finalisiert', variant: 'primary', darkText: false }],
  ['PDF_GENERIERUNG', { label: 'PDF wird erstellt', variant: 'info', darkText: true }],
  ['FINALISIERUNG_LAEUFT', { label: 'Finalisierung läuft', variant: 'info', darkText: true }],
  ['PDF_FEHLER', { label: 'PDF-Fehler', variant: 'danger', darkText: false }],
  ['TERMINATED', { label: 'Beendet', variant: 'dark', darkText: false }],
] as const);

/** Fallback fuer unbekannte oder fehlende Statuswerte. */
const UNKNOWN_STATUS: ContractStatusPresentation = {
  label: 'Unbekannt',
  variant: 'light',
  darkText: true,
};

/**
 * Liefert Text und Badge-Farbe zu einem Vertragsstatus.
 * Unbekannte Werte behalten ihren Rohtext, ein leerer Wert wird zu
 * "Unbekannt".
 */
export function describeContractStatus(status: string): ContractStatusPresentation {
  const known: ContractStatusPresentation | undefined = STATUS_PRESENTATIONS.get(status);
  if (known !== undefined) {
    return known;
  }
  return status === '' ? UNKNOWN_STATUS : { ...UNKNOWN_STATUS, label: status };
}
